import React, { useEffect, useState, useCallback } from "react";
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography, Toolbar, Button,
  CircularProgress, Box, IconButton, Tooltip, MenuItem, Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddIcon from "@material-ui/icons/Add";
import API from "../../../network/dashboardAPI";
import DeliveryRateDialog from "./component/DeliveryRateDialog";
import ActionButtons from "./component/ActionButtons";
import useAlert from "../../../services/useAlert";
import {
  DELIVERY_TYPES, ORDER_TYPES, deliveryTypeLabel,
  formatDistanceTo, formatAmountTo, formatWeightTo, formatFee, isFreeRow,
  readDistanceToKm, readWeightToGrams, readIsActive,
} from "./utils/helper";

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", padding: theme.spacing(2) },
  toolbar: { display: "flex", justifyContent: "space-between", marginBottom: theme.spacing(2) },
  filterField: { width: 170, marginLeft: theme.spacing(1) },
  tableContainer: { maxHeight: "calc(100vh - 320px)" },
  // MUI's size="small" table defaults to 0.875rem. Bumped to 1rem with a little
  // extra vertical padding to match — the admins reading this on large screens
  // found the default too small to scan.
  table: {
    "& .MuiTableCell-root": {
      fontSize: "1rem",
      padding: theme.spacing(1.25, 2),
    },
  },
  loadingRow: { textAlign: "center", padding: theme.spacing(4) },
  emptyRow: { textAlign: "center", padding: theme.spacing(4) },
  headerCell: { fontWeight: 600, backgroundColor: "#f5f5f5", fontSize: "1rem" },
  hint: { padding: theme.spacing(0, 2, 2, 2) },
  hintText: { fontSize: "0.95rem" },
  title: { fontSize: "1.35rem" },
  chip: { fontSize: "0.85rem", height: 26 },
  doorChip: { backgroundColor: "#ede7f6", color: "#4527a0" },
  pickupChip: { backgroundColor: "#e3f2fd", color: "#1565c0" },
  expressChip: { backgroundColor: "#fff3e0", color: "#e65100" },
  freeChip: { backgroundColor: "#e8f5e9", color: "#2e7d32", marginLeft: theme.spacing(1) },
  activeChip: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
  inactiveChip: { backgroundColor: "#eeeeee", color: "#616161" },
  inactiveRow: { opacity: 0.6 },
  feeCell: { fontWeight: 600 },
}));

const chipClassFor = (classes, deliveryType) => {
  if (deliveryType === "pickup") return classes.pickupChip;
  if (deliveryType === "express") return classes.expressChip;
  return classes.doorChip;
};

export default function DeliveryRatesList() {
  const classes = useStyles();
  const alert = useAlert();
  const [data, setData] = useState([]);
  // Order type is not a filter here: it mirrors the delivery type, so exposing
  // it would only offer a way to hide half the matrix. Every load covers both.
  const [deliveryType, setDeliveryType] = useState("");
  const [activeOnly, setActiveOnly] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const base = {};
      if (deliveryType) base.delivery_type = deliveryType;
      if (activeOnly !== "") base.is_active = activeOnly;

      // The union is client-side: the endpoint has no "all order types" value,
      // and dropping `type` would silently return the Normal rules only.
      const types = ORDER_TYPES.map((t) => t.value);

      const results = await Promise.all(types.map(async (type) => {
        const response = await API.getAllDeliveryRule({ ...base, type });
        return response.json();
      }));

      // The controller returns the rule list directly as `data`, already in
      // matrix order — not wrapped in {items, total}.
      const rows = [];
      const seen = new Set();
      const failures = [];
      results.forEach((res, index) => {
        if (!res.success) {
          failures.push(res.data || res.message || `order type ${types[index]}`);
          return;
        }
        (Array.isArray(res.data) ? res.data : []).forEach((row) => {
          // Guards the union: a backend that ignores `type` would otherwise
          // return the same rule once per request.
          if (seen.has(row.id)) return;
          seen.add(row.id);
          rows.push(row);
        });
      });

      // Partial failures still render what did load — a half-empty matrix the
      // admin can see beats a blank page that hides the rules that are live.
      setData(rows);
      if (failures.length) {
        alert.error("Error", `Failed to load delivery rules: ${failures.join("; ")}`);
      }
    } catch (error) {
      console.error("Error fetching delivery rules:", error);
      alert.error("Error", "Failed to load delivery rules");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryType, activeOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = () => { setSelectedItem(null); setDialogOpen(true); };
  const handleEdit = (item) => { setSelectedItem(item); setDialogOpen(true); };

  const handleDelete = async (row) => {
    const result = await alert.confirm(
      "Delete Delivery Rule?",
      // Hard delete, and the API refuses to remove the last rule for an order
      // type. Past orders keep the fee they were charged.
      `Permanently delete "${row.label || `rule ${row.id}`}" — ` +
      `${deliveryTypeLabel(row.deliveryType)}, ${formatDistanceTo(readDistanceToKm(row))}, ` +
      `${formatAmountTo(row.amountTo)}, ${formatWeightTo(readWeightToGrams(row))}? ` +
      `This cannot be undone.`,
      "Yes, Delete", "Cancel"
    );
    if (!result.isConfirmed) return;

    setDeleteLoading(row.id);
    try {
      const response = await API.deleteDeliveryRuleById(row.id);
      const res = await response.json();
      if (res.success) {
        alert.toast("Delivery rule deleted", "success");
        fetchData();
      } else {
        alert.error("Error", res.data || res.message || "Failed to delete");
      }
    } catch (error) {
      alert.error("Error", "Failed to delete delivery rule");
    }
    setDeleteLoading(null);
  };

  const columns = [
    { id: "id", label: "ID", minWidth: 50 },
    { id: "label", label: "Label", minWidth: 150 },
    { id: "deliveryType", label: "Delivery Type", minWidth: 120 },
    { id: "distanceToKm", label: "Distance up to", minWidth: 110 },
    { id: "amountTo", label: "Order total up to", minWidth: 120 },
    { id: "fee", label: "Fee", minWidth: 150 },
    { id: "weightToGrams", label: "Weight up to", minWidth: 150 },
    { id: "payment", label: "Payment", minWidth: 110 },
    { id: "status", label: "Status", minWidth: 90 },
    { id: "actions", label: "Actions", minWidth: 100 },
  ];

  return (
    <div className={classes.root}>
      <Paper elevation={1}>
        <Toolbar className={classes.toolbar}>
          <Typography variant="h6" className={classes.title}>Delivery Rates</Typography>
          <Box display="flex" alignItems="center">
            <TextField
              select className={classes.filterField} size="small" variant="outlined"
              label="Delivery type" value={deliveryType}
              onChange={(e) => setDeliveryType(e.target.value)}
            >
              <MenuItem value="">All types</MenuItem>
              {DELIVERY_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField
              select className={classes.filterField} size="small" variant="outlined"
              label="Status" value={activeOnly}
              onChange={(e) => setActiveOnly(e.target.value)}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="0">Inactive</MenuItem>
            </TextField>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData}><RefreshIcon /></IconButton>
            </Tooltip>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAdd}>
              Add Rule
            </Button>
          </Box>
        </Toolbar>

        <Box className={classes.hint}>
          <Typography variant="body2" color="textSecondary" className={classes.hintText}>
            Each row is one cell of the rate matrix. Only the upper bound of a band is
            stored, and an order falls in the smallest ceiling strictly greater than its
            value — so a 5 kg order is priced by the 20 kg band. Free delivery is a
            zero-fee row with a higher order-total ceiling, not a separate threshold.
          </Typography>
        </Box>

        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.id} className={classes.headerCell} style={{ minWidth: col.minWidth }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className={classes.loadingRow}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className={classes.emptyRow}>
                    <Typography color="textSecondary">No delivery rules found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    className={readIsActive(row) ? undefined : classes.inactiveRow}
                  >
                    <TableCell>{row.id}</TableCell>
                    <TableCell>
                      <strong>{row.label || "-"}</strong>
                      {isFreeRow(row) && (
                        <Chip size="small" label="Free" className={`${classes.chip} ${classes.freeChip}`} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={deliveryTypeLabel(row.deliveryType)}
                        className={`${classes.chip} ${chipClassFor(classes, row.deliveryType)}`}
                      />
                    </TableCell>
                    <TableCell>{formatDistanceTo(readDistanceToKm(row))}</TableCell>
                    <TableCell>{formatAmountTo(row.amountTo)}</TableCell>
                    <TableCell>{formatWeightTo(readWeightToGrams(row))}</TableCell>
                    <TableCell className={classes.feeCell}>{formatFee(row)}</TableCell>
                    <TableCell>
                      {Array.isArray(row.allowedPaymentMethod) && row.allowedPaymentMethod.length
                        ? row.allowedPaymentMethod.join(", ")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={readIsActive(row) ? "Active" : "Inactive"}
                        className={`${classes.chip} ${
                          readIsActive(row) ? classes.activeChip : classes.inactiveChip
                        }`}
                      />
                    </TableCell>
                    <TableCell>
                      <ActionButtons
                        row={row}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        deleteLoading={deleteLoading}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <DeliveryRateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchData}
        initialData={selectedItem}
      />
    </div>
  );
}
